import { SwaggerlessContext } from 'Context/SwaggerlessContext';
import React, { Component } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import Short from 'Components/Format/DateFormat/Short';
import Icon from 'Components/UI/Icon';
import Button from 'Components/UI/Button';
import DeleteIcon from '@material-ui/icons/Delete';
import { householdPlanSummaryUri } from 'Consts/advisorUris';
import { getIconSrc } from 'Helpers/PlanType';
import { noop } from 'Helpers/PropTypes';
import { setPlan } from 'State/actions';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import './index.css';
import { withHousehold } from 'Context/HouseholdContext';
import IconButton from '@material-ui/core/IconButton';
import Popover from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Popover';
import ListItem from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/ListItem';
import List from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/List';

const styles = theme => ({
  root: {
    fontSize: theme.typography.h2.fontSize,
    display: 'flex',
  },
  tableCell: {
    alignItems: 'center',
    display: 'flex',
    minWidth: '100%',
  },
  icon: {
    marginRight: 20,
  },
  link: {
    flexGrow: 1,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    minWidth: 0,
    marginRight: theme.spacing(3),
  },
  actionsAndTime: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  timeAndDelete: {
    paddingLeft: theme.spacing(1),
  },
  button: {
    minWidth: 'fit-content',
    whiteSpace: 'no-wrap',
  },
});

const mapDispatchToProps = dispatch => ({
  set: plan => dispatch(setPlan(plan)),
});

class PlanListEntry extends Component {
  constructor(props) {
    super(props);

    this.state = {
      submitting: false,
      popoverAnchorEl: null,
    };

    this.getPlan = this.getPlan.bind(this);
    this.removePlan = this.removePlan.bind(this);
    this.upload = this.upload.bind(this);
    this.determinePlanType = this.determinePlanType.bind(this);
    this.sendPlanToFiles = this.sendPlanToFiles.bind(this);
    this.handlePopoverOpen = this.handlePopoverOpen.bind(this);
    this.handlePopoverClose = this.handlePopoverClose.bind(this);
  }

  getPlan() {
    const { id, householdID, set } = this.props;
    set(null); // remove previous plan from memory, otherwise it shows initially until new plan is loaded.
    const { get, convert, openExternalPlan } = this.determinePlanType();
    if (openExternalPlan) {
      openExternalPlan();
    } else {
      get(householdID, id).then((res) => {
        const plan = res?.data;
        set(convert(plan));
      });
    }
  }

  handlePopoverOpen(event) {
    this.setState({ popoverAnchorEl: event.currentTarget });
  }

  handlePopoverClose() {
    this.setState({ popoverAnchorEl: null });
  }

  determinePlanType() {
    const {
      type,
      url,
    } = this.props;
    switch (type) {
      case 'external':
        return {
          get: async () => {},
          convert: () => {},
          openExternalPlan: () => {
            window.open(url, '_blank');
          },
        };
      default:
        throw new Error('No valid default for getting a plan.');
    }
  }

  removePlan() {
    const {
      id, householdID, onDelete,
    } = this.props;
    const { removePlan } = this.context;
    onDelete(id, () => removePlan(householdID, id));
  }

  upload() {
    const { uploadSecureFile, secureFileUploadURL } = this.props;
    uploadSecureFile(secureFileUploadURL);
  }

  sendPlanToFiles(opts = {}) {
    const {
      uploadSecureFile,
      secureFileUploadURL,
    } = this.props;
    const {
      sendPlanMessage,
    } = opts;

    if (!secureFileUploadURL) {
      throw new Error('secureFileUploadURL is missing');
    }

    return uploadSecureFile(secureFileUploadURL, { sendPlanMessage });
  }

  render() {
    const {
      name, type, lastModified, householdID, iconSrcOverride, classes, className, secureFileUploadURL, planMessageRelationship, id,
    } = this.props;
    const { submitting, popoverAnchorEl } = this.state;
    const isPopoverOpen = Boolean(popoverAnchorEl);
    const iconSrc = type === 'external' ? iconSrcOverride : getIconSrc(type);
    return (
      <tr className={classNames('PlanListEntry', className, classes.root)}>
        <td className={classNames('link', classes.tableCell)}>
          <Link
            to={householdPlanSummaryUri(householdID)}
            onClick={this.getPlan}
            className={classes.link}
            test={`plan-list-${id}-link`}
          >
            <Icon className={classes.icon} src={iconSrc} />
            <span>{name}</span>
          </Link>
          <div className={classes.actionsAndTime}>
            {planMessageRelationship
              ? (
                <React.Fragment>
                  <IconButton
                    size="small"
                    onClick={this.handlePopoverOpen}
                    test={`plan-list-${id}-share`}
                  >
                    <Icon icon="share" />
                  </IconButton>
                  <Popover
                    open={isPopoverOpen}
                    anchorEl={popoverAnchorEl}
                    onClose={this.handlePopoverClose}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                  >
                    <List>
                      <ListItem
                        button
                        onClick={() => {
                          this.sendPlanToFiles({ sendPlanMessage: true });
                          this.handlePopoverClose();
                        }}
                        disabled={submitting}
                        test={`plan-list-${id}-send-in-messages`}
                      >
                        {'Send in Messages'}
                      </ListItem>
                      <ListItem
                        button
                        onClick={() => {
                          this.sendPlanToFiles();
                          this.handlePopoverClose();
                        }}
                        test={`plan-list-${id}-save-to-files`}
                      >
                        {'Save to Files'}
                      </ListItem>
                    </List>
                  </Popover>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  {secureFileUploadURL !== '' && (
                    <Button
                      color="primary"
                      onClick={this.upload}
                      variant="contained"
                      padding="squished"
                      size="small"
                    >
                      {'Save to Files'}
                    </Button>
                  )}
                </React.Fragment>
              )}
            <Short
              date={lastModified}
              className={classes.timeAndDelete}
            />
            <IconButton
              color="primary"
              className={classes.timeAndDelete}
              onClick={this.removePlan}
            >
              <DeleteIcon />
            </IconButton>
          </div>
        </td>
      </tr>
    );
  }
}

PlanListEntry.contextType = SwaggerlessContext;

PlanListEntry.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  id: PropTypes.number.isRequired,
  lastModified: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  onDelete: PropTypes.func,
  uploadSecureFile: PropTypes.func,
  set: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  householdID: PropTypes.number.isRequired,
  url: PropTypes.string,
  iconSrcOverride: PropTypes.string,
  secureFileUploadURL: PropTypes.string,
  planMessageRelationship: PropTypes.string,
};

PlanListEntry.defaultProps = {
  onDelete: noop,
  uploadSecureFile: noop,
  url: '',
  iconSrcOverride: '',
  className: '',
  secureFileUploadURL: '',
  planMessageRelationship: '',
};

export default withHousehold(connect(() => ({}), mapDispatchToProps)(withStyles(styles)(PlanListEntry)));
